import type { TrafficDay, TrafficReport } from '../api/authoringApi'
import type { TrafficConnection } from '../api/operatorApi'

// A site's visit counts, as the Overview's Visits card reads them
// (docs/plan/site-traffic.md §8).
//
// **The rows are days and the card draws weeks**, so the summing is done
// here rather than asked of the server: the authoring read answers the
// rows that exist and nothing else. A week is a fact about the calendar
// the numbers were collected in, so "today" is the report's and never the
// reader's clock. Every date is a civil date, `YYYY-MM-DD`, and all the
// arithmetic is on those — never on instants — so a day is a day whatever
// a zone does to midnight twice a year.

/** How many complete weeks the card draws before the current one. */
export const COMPLETE_WEEKS = 12

/** One Monday-to-Sunday week, summed from the rows that fall in it. */
export interface Week {
  /** The Monday. */
  start: string
  /** The Sunday. */
  end: string
  /** The week the report's `today` is in, so not over yet. */
  partial: boolean
  /** How many rows it was summed from. None is unknown, not zero. */
  rows: number
  /**
   * How many of its days have no row and should have one by now: days the
   * host was never asked about, or answered nothing for. While the report
   * is `current` that is every such day up to `through`; in every other
   * state it is every such day before today, because nothing is coming
   * for them. Days before `since`, which no collection reached, and today
   * and the days after it, which nobody could have collected yet, are
   * never missing.
   */
  missing: number
  /**
   * How many of its days before today are after `through` (all of them,
   * without one) and have no row, while the report is `current`: days the
   * collector has not reached yet. Yesterday is one every night until the
   * run, so it is not a gap. In any other state nothing is pending: a
   * report that is `stale` or `disconnected` is not about to catch up, and
   * a day it has not reached is missing, however long ago it was.
   */
  pending: number
  /**
   * Summed from some of its days and not all: it has rows, and days
   * without one. Its figures are a floor, not the week's count, and the
   * card draws it so. A week with no rows at all is a gap instead.
   */
  incomplete: boolean
  visits: number
  pageViews: number
}

/** A civil date as a number of days, for arithmetic only. */
function ordinal(day: string): number {
  const [year, month, date] = day.split('-').map(Number)
  return Date.UTC(year, month - 1, date) / 86_400_000
}

function fromOrdinal(value: number): string {
  return new Date(value * 86_400_000).toISOString().slice(0, 10)
}

/** `day` moved by `days`, either way. */
export function addDays(day: string, days: number): string {
  return fromOrdinal(ordinal(day) + days)
}

/** The Monday of the week `day` is in. */
export function mondayOf(day: string): string {
  // 1970-01-01 was a Thursday, so day 0 is three days after a Monday.
  const index = ordinal(day)
  return fromOrdinal(index - ((((index + 3) % 7) + 7) % 7))
}

/** What `weeksOf` reads from a report: its rows, its calendar and its state. */
export type WeeksSource = Pick<TrafficReport, 'days' | 'today' | 'state' | 'through' | 'since'>

/**
 * The twelve complete weeks before `today`'s and `today`'s own, oldest
 * first, each summed from the rows dated inside it. A row outside them is
 * not drawn.
 *
 * `through` is the report's, the last day the collector has finished.
 * **A day after it is pending only while the report is `current`**: that
 * is yesterday from midnight until the night's run, and calling it "not
 * collected" would hatch this week every night, and early on a Monday
 * the last one too. A `stale` or `disconnected` report is not waiting on
 * a run, so a day it has not reached is missing like any other, and a
 * week summed from the days before it is drawn short of them rather than
 * passed off as whole. Otherwise a site disconnected on a Thursday would
 * show that week's four days as the week's count for as long as the card
 * draws it.
 *
 * `since` is the earliest day ever collected for the site, and a day
 * before it is neither: no collection reached back that far, so the week
 * history begins in is drawn from the days it has.
 *
 * `today` is the report's: the date where the numbers were collected,
 * not where they are read. A reader in London on a New Zealand Monday
 * morning is still in Sunday, and a week taken from their clock would put
 * a week the collector has finished in "this week" and leave the
 * headline's four weeks one short of the platform's.
 */
export function weeksOf({ days, today, state, through, since }: WeeksSource): Week[] {
  const current = mondayOf(today)
  const weeks: Week[] = []
  for (let back = COMPLETE_WEEKS; back >= 0; back--) {
    const start = addDays(current, -7 * back)
    weeks.push({
      start,
      end: addDays(start, 6),
      partial: back === 0,
      rows: 0,
      missing: 0,
      pending: 0,
      incomplete: false,
      visits: 0,
      pageViews: 0,
    })
  }
  const collected = new Set<string>()
  for (const day of days) {
    const week = weeks.find((candidate) => candidate.start <= day.date && day.date <= candidate.end)
    if (!week) continue
    collected.add(day.date)
    week.rows++
    week.visits += day.visits
    week.pageViews += day.pageViews
  }
  for (const week of weeks) {
    for (let date = week.start; date <= week.end && date < today; date = addDays(date, 1)) {
      if (collected.has(date) || (since !== undefined && date < since)) continue
      if (state === 'current' && (through === undefined || date > through)) week.pending++
      else week.missing++
    }
    week.incomplete = week.rows > 0 && week.missing > 0
  }
  return weeks
}

/** What the card leads with: the last four complete weeks, and how they compare. */
export interface Headline {
  /** Whether any of the four weeks has a row. Without one there is no figure to give. */
  known: boolean
  visits: number
  pageViews: number
  /**
   * The change in visits against the four complete weeks before, as a
   * fraction: 0.12 is twelve per cent up. Absent unless every day of
   * both windows has a row (none missing, none still pending, and none
   * before history began), and
   * the earlier one has visits to compare against.
   */
  change?: number
}

export function headline(weeks: Week[]): Headline {
  const complete = weeks.filter((week) => !week.partial)
  const recent = complete.slice(-4)
  const before = complete.slice(-8, -4)
  const sum = (span: Week[], measure: 'visits' | 'pageViews') => span.reduce((total, week) => total + week[measure], 0)
  const result: Headline = {
    known: recent.some((week) => week.rows > 0),
    visits: sum(recent, 'visits'),
    pageViews: sum(recent, 'pageViews'),
  }
  // A comparison is a claim about visitors, and a day with no row is a
  // fact about collection: a site whose host went unread for a week would
  // read as a fall, and one whose history began inside the earlier
  // window as a rise from nothing. So the change is drawn only when every
  // day of all eight weeks has a row, and otherwise not at all, rather
  // than scaled up from the days that were. A pending day takes it too:
  // on a Monday before the night's run the last week's Sunday is not in
  // yet, and the comparison waits for the run rather than counting a wait
  // as a fall. Rows, not `missing`, because a day before `since` is
  // neither missing nor pending and is still not a day with visitors.
  const covered = before.length === 4 && [...before, ...recent].every((week) => week.rows === 7)
  const earlier = sum(before, 'visits')
  if (covered && earlier > 0) result.change = (result.visits - earlier) / earlier
  return result
}

/**
 * This week so far, for the card's other headline: the same figures as
 * its bar, never compared, because a week that is not over has nothing
 * like-for-like to be compared against.
 */
export function thisWeek(weeks: Week[]): Headline {
  const week = weeks.find((candidate) => candidate.partial)
  if (!week) return { known: false, visits: 0, pageViews: 0 }
  return { known: week.rows > 0, visits: week.visits, pageViews: week.pageViews }
}

/** The change, in words, with no sign the reader has to decode. */
export function changeText(change: number): string {
  const percent = Math.round(Math.abs(change) * 100)
  if (percent === 0) return 'About the same as the four weeks before'
  return `${change > 0 ? 'Up' : 'Down'} ${percent}% on the four weeks before`
}

/**
 * The line under the bars, built from what the report declares about how
 * it was counted, so no provider is ever named.
 */
export function caveat(report: Pick<TrafficReport, 'sampled' | 'method'>): string {
  const parts: string[] = []
  if (report.sampled) parts.push('Counts are estimates.')
  if (report.method === 'browser') parts.push('Counts people’s browsers, not search engines, bots, and some visitors block counting.')
  if (report.method === 'server') parts.push('Counts every request, including search engines.')
  return parts.join(' ')
}

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

/** `2026-10-12` as "12 October 2026", the same in every locale and zone. */
export function dayText(day: string): string {
  const [year, month, date] = day.split('-').map(Number)
  return `${date} ${MONTHS[month - 1]} ${year}`
}

/**
 * An instant as the day it fell on where it is read, written as `dayText`
 * writes a civil date: when an operator stated something, not a day of
 * visits, so the reader's own calendar is the right one.
 */
export function instantDayText(instant: string): string {
  const at = new Date(instant)
  const pad = (value: number) => String(value).padStart(2, '0')
  return dayText(`${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`)
}

/** A week's first day, short: "6 Oct". */
export function weekStartText(week: Week): string {
  const [, month, date] = week.start.split('-').map(Number)
  return `${date} ${MONTHS[month - 1].slice(0, 3)}`
}

/** A week as a span of days: "6 to 12 October", or across a month "29 September to 5 October". */
export function weekText(week: Week): string {
  const [, startMonth, startDate] = week.start.split('-').map(Number)
  const [, endMonth, endDate] = week.end.split('-').map(Number)
  const span =
    startMonth === endMonth
      ? `${startDate} to ${endDate} ${MONTHS[endMonth - 1]}`
      : `${startDate} ${MONTHS[startMonth - 1]} to ${endDate} ${MONTHS[endMonth - 1]}`
  return week.partial ? `This week, ${span}` : span
}

/** A count as the reader's locale writes it. */
export function countText(value: number): string {
  return value.toLocaleString()
}

/** Visits, with the noun agreeing. */
export function visitsText(value: number): string {
  return `${countText(value)} ${value === 1 ? 'visit' : 'visits'}`
}

/** Page views, with the noun agreeing. */
export function pageViewsText(value: number): string {
  return `${countText(value)} ${value === 1 ? 'page view' : 'page views'}`
}

/** How many of a week's days were not collected: "3 days not collected". */
export function missingText(week: Week): string {
  return `${week.missing} ${week.missing === 1 ? 'day' : 'days'} not collected`
}

/** One week as a sentence: what a bar says when it is pointed at, focused or tapped. */
export function weekDetail(week: Week): string {
  if (week.rows === 0) return `${weekText(week)}: no numbers for this week.`
  const figures = `${weekText(week)}${week.partial ? ' so far' : ''}: ${visitsText(week.visits)}, ${pageViewsText(week.pageViews)}.`
  return week.incomplete ? `${figures} ${missingText(week)}.` : figures
}

/** A week as the table's first cell names it, saying when its figures are short of days. */
export function weekCellText(week: Week): string {
  return week.incomplete ? `${weekText(week)} (${missingText(week)})` : weekText(week)
}

/** What a connection's health row says, in words: absent until a run has checked it. */
export function healthText(connection: Pick<TrafficConnection, 'configured'>): string {
  if (connection.configured === undefined) return 'Not checked yet'
  return connection.configured ? 'Configured' : 'No credentials'
}

/** One day of the sparkline: its visits, or null for a day nothing was collected for. */
export interface DailyPoint {
  date: string
  visits: number | null
}

/**
 * Every day from the first week's Monday to the last day collected,
 * within the weeks drawn, with the days that have no row as null.
 *
 * It stops at `through` (or the newest row, without one) rather than at
 * today: the days after it have not been asked for yet, and drawing them
 * would end every line with a fall that is only a wait.
 */
export function dailyVisits(days: TrafficDay[], weeks: Week[], through: string | undefined): DailyPoint[] {
  if (weeks.length === 0) return []
  const first = weeks[0].start
  const newest = days.reduce<string | undefined>((latest, day) => (!latest || day.date > latest ? day.date : latest), undefined)
  const lastDrawn = weeks[weeks.length - 1].end
  const last = [through ?? newest, lastDrawn].filter((day): day is string => Boolean(day)).sort()[0]
  if (!last || last < first) return []
  const byDate = new Map(days.map((day) => [day.date, day.visits]))
  const points: DailyPoint[] = []
  for (let date = first; date <= last; date = addDays(date, 1)) {
    points.push({ date, visits: byDate.get(date) ?? null })
  }
  // A line made only of gaps says nothing a line should.
  return points.some((point) => point.visits !== null) ? points : []
}
